import React, { useState, useEffect, Fragment, useCallback } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { createRoot } from '@wordpress/element';
import { Tab, TabsList, TabPanel, Tabs } from '@mui/base'; // https://mui.com/base-ui/react-tabs/
import { displayErrorMessage, ConnectionContext, getConnectionInfo, getEndpoint, displayLoader } from './utils';
import { getStoredData } from "./utils/data-pulls";
import { AcaSelect } from "./components/forms/acaSelect";
import { Option } from '@mui/base/Option';
import { DateGrid } from "./components/forms/dateGrid";
import { MatchGrid } from "./components/matchGrid";
import { Sidebar } from "./components";
import { LiveGamesWidget } from "./components/widgets/liveGamesWidget";
import { UpcomingGamesWidget } from "./components/widgets/upcomingGamesWidget";
import { StandingsAndBracket } from "./components/tables/standingsAndBracket";
import { ClubBox } from "./components/clubBox";
import { PlayerBox } from "./components/playerBox";
import { NewsItem } from "./components/widgets/newsItem";

// import function to register Swiper custom elements
import { register } from 'swiper/element/bundle';
// register Swiper custom elements
register();


const CompetitionApp = (props) => {
    const { 
        id = '',
        name = '',
        ...restProps 
    } = props;

    // Error and load handling
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);

    // Connection
    const [connectionInfo, setConnectionInfo] = useState(null);

    // Competition
    const [ competition, setCompetition ] = useState(null);
    const [ nestedCompetitions, setNestedCompetitions ] = useState(null);
    const [ seasons, setSeasons ] = useState(null);
    const [ selectedSeason, setSelectedSeason ] = useState(null);
    const [ news, setNews ] = useState(null);

    // Clubs
    const [ clubs, setClubs ] = useState(null);

    // Game Schedule
    const [ scheduleDates, setScheduleDates ] = useState(null);
    const [ selectedScheduleDate, setSelectedScheduleDate ] = useState(null);

    // Standings
    const [ standings, setStandings ] = useState(null);

    // Stats
    const [ filteredClub, setFilteredClub ] = useState(null);
    const [ storedPlayerLinks, setStoredPlayerLinks ] = useState([]);

    const [ statGoals, setStatGoals ] = useState(null);
    const [ goalsLoaded, setGoalsLoaded ] = useState(false);
    const [ statRedCards, setStatRedCards ] = useState(null);
    const [ redCardsLoaded, setRedCardsLoaded ] = useState(false);
    const [ statYellowCards, setStatYellowCards ] = useState(null);
    const [ yellowCardsLoaded, setYellowCardsLoaded ] = useState(false);

    // Layout pieces
    const [ selectedTab, setSelectedTab ] = useState(1);
    const [clubSlider, setClubSlider] = useState(null);
    const sliderRef = useCallback(node => {
        setClubSlider(node)
    });


    /**
     * Get competition information and connection info
     */
    useEffect(() => {

        // Get our connection info
        getConnectionInfo().then(
            (result) => {
                setConnectionInfo(result.data);
            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

        // Get competition list
        let args = [
            {
                key: 'competition',
                id: id
            },
            {
                key: 'nestedCompetitions',
                id: id
            },
            {
                key: 'scheduleDates',
                type: 'specific' // Change this to specific
            },
            {
                key: 'standings',
                type: 'specific'
            }
        ];
        getStoredData(args).then(
            (result) => {

                // Set Competitions & Seasons
                if ( result.data.competition ) {
                    setCompetition(result.data.competition);
                    if ( result.data.competition.hasOwnProperty('news') && result.data.competition.news ) {
                        setNews(result.data.competition.news);
                    }
                    if ( result.data.competition.hasOwnProperty('seasons') && result.data.competition.seasons ) {
                        let seasonsArray = Object.keys(result.data.competition.seasons).map((key) => result.data.competition.seasons[key]);
                        if ( result.data.competition.hasOwnProperty('seasons_order') ) {
                            let sortedSeasonsArray = [];
                            let sortingKey = result.data.competition.seasons_order;
                            seasonsArray.forEach((season) => {
                                const index = sortingKey.indexOf(season.name);
                                sortedSeasonsArray[index] = season;
                            });
                            seasonsArray = sortedSeasonsArray;
                        }
                        setSeasons(seasonsArray);
                        setSelectedSeason(seasonsArray[0]);
                    }
                    
                }

                // Set child competitions if they exist
                if ( result.data.nestedCompetitions ) {
                    setNestedCompetitions(result.data.nestedCompetitions);
                }

                // Set loaded
                setIsLoaded(true);

            }
        ).catch(
            (error) => {
                setError(error);
            }
        );
    }, []);

    /**
     * When season is available:
     * 1 - Get the new list of schedule dates
     * 2 - Get the list of teams for that season
     */
    useEffect(() => {
        if ( !competition || !selectedSeason ) return;
        if ( !isLoaded ) return;
        setIsLoaded(false);

        let args = [
            {
                key: 'scheduleDates',
                type: 'specific',
                competition: competition.id,
                season: selectedSeason.name,
            },
            {
                key: 'standings',
                type: 'specific',
                competition: competition.id,
                season: selectedSeason.name,
            },
            {
                key: 'clubs',
                type: 'competition',
                id: selectedSeason.uid,
            }
        ];

        // Get our stored data
        getStoredData(args).then(
            (result) => {

                // Set schedule
                if ( result.data.scheduleDates ) {
                    setScheduleDates(result.data.scheduleDates);
                } else {
                    setScheduleDates(null);
                    setSelectedScheduleDate(null);
                }

                // Set standings
                if ( result.data.standings ) {
                    setStandings(result.data.standings);
                } else {
                    setStandings(null);
                }

                // Set clubs
                if ( result.data.clubs ) {
                    setClubs(result.data.clubs);
                }

                // Set loaded
                setIsLoaded(true);

            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

    }, [selectedSeason, competition]);


    /**
     * When we have connection information and the selected season:
     * 1 - Get the competition stats
     * 2 - Update when a filtered club is selected
     */
    useEffect(() => {
        if ( !connectionInfo || !selectedSeason ) return;
        setGoalsLoaded(false);
        setRedCardsLoaded(false);
        setYellowCardsLoaded(false);

        // Set up endpoints
        let goalsEndpoint, redCardEndpoint, yellowCardEndpoint;
        let endpointVariables;
        let endpointParams = {
            organizationIdFilter: connectionInfo.organization_id
        };
        if ( filteredClub ) {
            endpointVariables = {
                competitionId: selectedSeason.uid,
                teamId: filteredClub,
                tenant: connectionInfo.tenant
            };
            goalsEndpoint = getEndpoint('competition_stats_team_goals', endpointVariables, endpointParams, connectionInfo.environment);
            redCardEndpoint = getEndpoint('competition_stats_team_redCards', endpointVariables, endpointParams, connectionInfo.environment);
            yellowCardEndpoint = getEndpoint('competition_stats_team_yellowCards', endpointVariables, endpointParams, connectionInfo.environment);
        } else {
            endpointVariables = {
                competitionId: selectedSeason.uid,
                tenant: connectionInfo.tenant
            };
            goalsEndpoint = getEndpoint('competition_stats_goals', endpointVariables, endpointParams, connectionInfo.environment);
            redCardEndpoint = getEndpoint('competition_stats_redCards', endpointVariables, endpointParams, connectionInfo.environment);
            yellowCardEndpoint = getEndpoint('competition_stats_yellowCards', endpointVariables, endpointParams, connectionInfo.environment);
        }
        
        // Fetch Goals
        fetch(goalsEndpoint, {
            method: 'GET',
            headers: {
                'API_KEY': connectionInfo.api_key
            },
        })
          .then(res => res.json())
          .then(
            (result) => {
                if ( !result.hasOwnProperty('messages') ) {
                    setStatGoals(result);
                }
                setGoalsLoaded(true);
            },
            (error) => {
              console.log(error);
            }
          );
        
          // Fetch Red Cards
          fetch(redCardEndpoint, {
              method: 'GET',
              headers: {
                  'API_KEY': connectionInfo.api_key
              },
          })
            .then(res => res.json())
            .then(
              (result) => {
                  if ( !result.hasOwnProperty('messages') ) {
                      setStatRedCards(result);
                  }
                  setRedCardsLoaded(true);
              },
              (error) => {
                console.log(error);
              }
            );
        
            // Fetch Yellow Cards
            fetch(yellowCardEndpoint, {
                method: 'GET',
                headers: {
                    'API_KEY': connectionInfo.api_key
                },
            })
              .then(res => res.json())
              .then(
                (result) => {
                    if ( !result.hasOwnProperty('messages') ) {
                        setStatYellowCards(result);
                    }
                    setYellowCardsLoaded(true);
                },
                (error) => {
                  console.log(error);
                }
              );

    }, [connectionInfo, selectedSeason, filteredClub]);


    /**
     * When we have all stat data from the Live API:
     * 1 - Query our stored data again to get the player URLs
     */
    useEffect(() => {
        if ( !statGoals || !statRedCards || !statYellowCards ) return;

        let mergedStats = statGoals.concat(statRedCards, statYellowCards);
        let lineup = {};
        mergedStats.forEach((stat) => {
            if ( !lineup.hasOwnProperty(stat.player.personId) ) {
                lineup[stat.player.personId] = stat.player;
            }
        });

        let args = [
            { 
                key: 'players',
                searchBy: 'lineup',
                lineup: lineup
            }
        ];

        // Get our stored data
        getStoredData(args).then(
            (result) => {

                // Set player data
                if ( result.data.players ) {
                    setStoredPlayerLinks(result.data.players);
                }
                
            }
        ).catch(
            (error) => {
                setError(error);
            }
        );
    }, [statGoals, statRedCards, statYellowCards]);


    // Initialize Swiper Slider if it exists
    useEffect(() => {
        if ( !clubSlider ) return;

        // swiper parameters
        const swiperParams = {
            slidesPerView: 2,
            slidesPerGroup: 2,
            navigation: {
                nextEl: '.acaSliderBtn--next',
                prevEl: '.acaSliderBtn--prev',
              },
            freeMode: true,
            spaceBetween: 20,
            breakpoints: {
                768: {
                    slidesPerView: 3
                },
                1023: {
                    slidesPerView: 4
                },
                1199: {
                    slidesPerView: 6
                }
            },
            on: {
                init() {
                // ...
                },
            },
        };

        Object.assign(clubSlider, swiperParams);
        clubSlider.initialize();

    }, [clubSlider]);


    /**
     * Handle season changes
     */
    const handleSeasonChange = (event, value) => {
        // Use the selected season's name to get the proper season
        const newSeason = seasons.find((season) => season.name == value);
        setSelectedSeason(newSeason);
        setSelectedTab(1); // Reset tabs on season change
    }

    /**
     * Handle nested competition changes
     */
    const handleNestedCompetitionsChange = (event, value) => {
        window.location.href = value;
    }


    /**
     * Handle selected date changes
     */
    const handleDateChange = (date = null) => {
        if ( !date ) return;
        setSelectedScheduleDate(date);
    }


    /**
     * Handle tab changes
     */
    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };


    /**
     * Handle filtering by club on the stats tab
     */
    const handleClubFilter = (uid) => {
        setFilteredClub(uid);
    };


    // Display an error message if there's a problem
    if ( error ) {
        console.log(error);
        return displayErrorMessage();
    }


    return (
        <ConnectionContext.Provider value={connectionInfo}>
            <Tabs className="acaPage acaPage--competition" slots={{ root: 'section' }} value={ selectedTab }  onChange={handleTabChange}>
                
                { /** Header */ }
                <div className="acaHeader acaHeader--hasBG acaHeader--competition">
                    <div className="acaWrapper acaHeader__inner">
                        <div className="acaHeader__main">
                            <h1 className="acaH1 acaHeader__title">{name}</h1>
                            <p className="acaH3 acaHeader__desc">{
                                nestedCompetitions && nestedCompetitions.length ? 'Select a season and region to view schedule and stats' : 'Select a season to view schedule and stats'
                            }</p>
                        </div>
                        <div className="acaHeader__aside">

                            { /** Nested Competitions Select */ }
                            { nestedCompetitions && nestedCompetitions.length ? (
                                <AcaSelect extraClasses="acaSelect--fullWidth acaSelect--hasPlaceholder" placeholder="Select a region" onChange={handleNestedCompetitionsChange}>
                                    { nestedCompetitions.map((c, i) => (
                                        <Option key={i} value={c.url}>{c.name}</Option>
                                    ))}
                                </AcaSelect>
                            ) : '' }

                            { /** Seasons Select */ }
                            { seasons && seasons.length ? (
                                <AcaSelect extraClasses="acaSelect--fullWidth" onChange={handleSeasonChange} defaultValue={selectedSeason ? selectedSeason.name : ''} id="schedule-season" name="schedule-season">
                                    { seasons.map((season, i) => (
                                        <Option key={i} value={season.name}>{season.name}</Option>
                                    ))}
                                </AcaSelect>
                            ) : '' }
                        </div>
                        
                        <TabsList className="acaHeader__tabs acaLargeTabs" >
                            <Tab value={1}>League Information</Tab>
                            { scheduleDates  ? (
                                <Tab value={2}>Game Schedule</Tab>
                            ) : '' }
                            <Tab value={3}>Stats</Tab>
                            { standings || ( selectedSeason && selectedSeason.bracket_chart && selectedSeason.bracket_chart !== null ) ? (
                                <Tab value={4}>
                                    { selectedSeason && selectedSeason.hasOwnProperty('type') && selectedSeason.type == 'bracket' ? 'Bracket' : 'Standings' }
                                </Tab>
                            ) : '' }
                            <Tab value={5}>News</Tab>
                        </TabsList>
                    </div>
                </div>

                { /** Main Content */ }
                
                { /** League Information */ }
                    <TabPanel value={1}>
                        <div className="acaWrapper acaMain">
                            <ErrorBoundary fallback={displayErrorMessage()}> 
                                { competition ? (
                                    <>
                                        {
                                            competition.info.buttons ? (
                                                <div className="competitionButtons">
                                                    { competition.info.buttons.map((button, i) => (
                                                        <Fragment key={i}>
                                                            {
                                                                button.hasOwnProperty('link') ? (
                                                                    <a 
                                                                        href={ button.link.url } 
                                                                        className={ "acaBtn acaBtn--large acaBtn--solid acaBtn--" + button.colour + " acaBtn--" + button.icon + "RegularIcon" }
                                                                        target={ button.link.target }
                                                                    >
                                                                        { button.link.title }
                                                                    </a>
                                                                ) : ''
                                                            }
                                                        </Fragment>
                                                    )) }
                                                </div>
                                            ) : ''
                                        }
                                        {
                                            competition.info.information ? (
                                                <div className="competitionInformation" dangerouslySetInnerHTML={{ __html: competition.info.information } }>
                                                </div>
                                            ) : ''
                                        }
                                    </>
                                ) : '' }
                            </ErrorBoundary>
                        </div>


                        { /** League Teams */ }
                        { clubs ? (
                            <div className="competitionTeams__section">
                                <div className="acaWrapper acaMain ">
                                    <ErrorBoundary fallback={displayErrorMessage()}>
                                        <h2 className="acaH1 competitionTeams__title">League Teams</h2>
                                        <div className="competitionTeams">
                                            { clubs.map((club, i) => (
                                                <ClubBox key={i} data={club} />
                                            )) }
                                        </div>
                                    </ErrorBoundary>
                                </div>
                            </div>
                        ) : '' }
                        
                    </TabPanel>

                    { /** Game Schedule */ }
                    <TabPanel value={2}>

                            <section className="acaPage acaPage--withSidebar acaPage--inner">

                                <div className="acaMain">
                                    <ErrorBoundary fallback={displayErrorMessage()}> 
                                        <DateGrid dates={scheduleDates} onChange={handleDateChange}></DateGrid>
                                        { selectedScheduleDate != null ? (
                                            <div className="acaWrapper acaWrapper--withPadding acaScheduleGrid__wrapper">
                                                <h3 className="acaH2 acaScheduleGrid__title">{ selectedScheduleDate.toLocaleString("en-US", { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }) }</h3>
                                                <MatchGrid season={selectedSeason.name} competition={competition.id} date={selectedScheduleDate} extraClasses="acaScheduleGrid--2Col" />
                                            </div>
                                        ) : (
                                            <>
                                                { displayErrorMessage('There are no dates available for this competition season. Please check back later.', 'acaError--light') }
                                            </>
                                        ) }
                                    </ErrorBoundary>
                                </div>

                                <Sidebar soccerImg>
                                    <ErrorBoundary fallback={displayErrorMessage()}>
                                        <LiveGamesWidget />
                                    </ErrorBoundary>
                                    <ErrorBoundary fallback={displayErrorMessage()}>
                                        { selectedSeason ? (
                                            <UpcomingGamesWidget type="competition" competition={id} />
                                        ) : '' }
                                    </ErrorBoundary>
                                </Sidebar>

                            </section>
                        
                    </TabPanel>

                    { /** Stats */ }
                    <TabPanel value={3}>
                        <ErrorBoundary fallback={displayErrorMessage()}> 
                            
                            { clubs ? (
                                <div className="statsFilter">
                                    <div className="acaWrapper">
                                        <div className="acaWidget acaWidget--statsFilter">

                                            <div className="acaWidget__header">
                                                <div className="acaWidget__headerMain">
                                                    <h4 className="acaH3 acaWidget__title">
                                                        Filter By Teams
                                                    </h4>
                                                </div>
                                                <div className="acaWidget__headerAside">
                                                    <div className="acaSliderBtns acaSliderBtns--dark">
                                                        <button className="acaSliderBtn acaSliderBtn--prev"></button>
                                                        <button className="acaSliderBtn acaSliderBtn--next"></button>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="acaWidget__inner">
                                                <swiper-container
                                                    init="false"
                                                    ref={sliderRef}
                                                >
                                                    <swiper-slide>
                                                        <ClubBox link={false} selected={filteredClub == null ? true : false} onClick={() => handleClubFilter(null)} custom="all-teams" />
                                                    </swiper-slide>
                                                    {
                                                        clubs.map((club, i) => (
                                                            <swiper-slide key={i}>
                                                                <ClubBox link={false} selected={filteredClub == club.uid ? true : false} onClick={() => handleClubFilter(club.uid)} data={club} />
                                                            </swiper-slide>
                                                        ) )
                                                    }
                                                </swiper-container>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            ) : '' }


                            { /** Stats Listings */ }
                            <div className="acaMain acaWrapper competitionStats__section">

                                <StatColumn 
                                    slug="goals" 
                                    title="Goals"
                                    stats={statGoals}
                                    statsLoaded={goalsLoaded}
                                    storedPlayerLinks={storedPlayerLinks}
                                />

                                <StatColumn 
                                    slug="red" 
                                    title="Red Cards"
                                    stats={statRedCards}
                                    statsLoaded={redCardsLoaded}
                                    storedPlayerLinks={storedPlayerLinks}
                                />

                                <StatColumn 
                                    slug="singleYellow" 
                                    title="Yellow Cards"
                                    stats={statYellowCards}
                                    statsLoaded={yellowCardsLoaded}
                                    storedPlayerLinks={storedPlayerLinks}
                                />

                            </div>

                            { /** Directory CTA */ }
                            <div className="acaCTA">
                                <div className="acaWrapper acaWrapper--withPadding acaCTA__inner">
                                    <div className="acaCTA__content">
                                        <h2 className="acaH2">Looking for a specific player?</h2>
                                    </div>
                                    <div className="acaCTA__aside">
                                        <a 
                                            href={ typeof acaDirectoryURL !== 'undefined' ? acaDirectoryURL + '?view=players' : '' } 
                                            className="acaBtn acaBtn--solid acaBtn--color1 acaBtn--arrowRightIcon">
                                            See the player directory
                                        </a>
                                    </div>
                                </div>
                            </div>

                        </ErrorBoundary>
                    </TabPanel>

                    { /** Standings */ }
                    <TabPanel value={4}>
                        <ErrorBoundary fallback={displayErrorMessage()}> 
                            <StandingsAndBracket season={selectedSeason} standings={standings} clubData={clubs} />
                        </ErrorBoundary>
                    </TabPanel>

                    { /** News */ }
                    <TabPanel value={5}>
                        <div className="acaMain acaWrapper">
                            { news ? (
                                <ErrorBoundary fallback={displayErrorMessage('No news currently available')}> 
                                    <div className="acaWidget acaWidget--news acaWidget--news3Col">

                                        <div className="acaWidget__header">
                                            <div className="acaWidget__headerMain">
                                                <h2 className="acaH2 acaWidget__title">League News</h2>
                                            </div>
                                            { competition && competition.hasOwnProperty('newsLink') && competition.newsLink ? (
                                                <div className="acaWidget__headerAside">
                                                    <a href={competition.newsLink} className="acaBtn acaBtn--solid acaBtn--small acaBtn--grey acaBtn--arrowRightIcon">See all league news</a>
                                                </div>
                                            ) : '' }
                                        </div>
                                        
                                        <div className="acaWidget__inner">
                                            { news.map((item, i) => (
                                                <NewsItem key={i} data={item} />
                                            )) }
                                        </div>

                                    </div>
                                </ErrorBoundary>
                            ) : '' }
                        </div>
                    </TabPanel>
            </Tabs>
        </ConnectionContext.Provider>
    );
}


const StatColumn = (props) => {
    const { 
        slug = '',
        title = '',
        stats = null,
        statsLoaded = false,
        storedPlayerLinks = null,
        ...restProps 
    } = props;

    const [ displayButton, setDisplayButton ] = useState(false);
    const [ toggle, setToggle ] = useState(false);

    /**
     * Set up whether the toggle button should display
     */
    useEffect(() => {
        if ( !stats ) return;
        setDisplayButton(stats.length > 5 ? true : false);
        setToggle(false);
    }, [stats])


    /**
     * Handle the button toggling
     */
    const handleToggle = () => {
        setToggle(prev => !prev);
    }



    /**
     * Find the player URL from an ID
     */
    const getPlayerURL = (id) => {
        let player = storedPlayerLinks.find((item) => item.uid == id);
        if ( player ) {
            return player.url;
        } else {
            return null;
        }
    }

    return (
        <div className="competitionStats">
            <h3 className="acaH3 competitionStats__title">{title}</h3>
            { statsLoaded ?
                stats && stats.length ? 
                    stats.map((stat, i) => (
                        <Fragment key={i}>
                            { toggle || i < 5 ? (
                                <PlayerBox 
                                    data={stat} 
                                    type="stats" 
                                    stat={slug}
                                    link={ getPlayerURL(stat.player.personId) }
                                />
                            ) : '' }
                        </Fragment>
                        
                    )) 
                    : displayErrorMessage('No stats available yet')
                : displayLoader('acaLoader--fill') }
            { displayButton ? (
                <button className={"acaBtn acaBtn--outline acaBtn--grey acaBtn--" + (toggle ? 'angleUpIcon' : 'angleDownIcon')} onClick={() => handleToggle()}>
                    { toggle ? "See less" : "See more" }
                </button>
            ) : '' }
        </div>
    );
}



export default CompetitionApp;

// Render Competition Page
const appContainer = document.getElementById('competitionApp');
const root = createRoot(appContainer);
const id = appContainer.dataset.id;
const name = appContainer.dataset.name;
root.render(<CompetitionApp id={id} name={name} />);